import { useEffect, useState, useMemo } from 'react';
import { shiftAPI, employeesAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import {
  CalendarClock, Plus, Edit, Trash2, Save, X,
  Sun, Moon, Coffee, AlertTriangle, ChevronRight, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const blankDay = () => ({ start: '', end: '', end_next_day: false, is_off: false, is_overtime: false });
const blankTemplate = () => ({
  name: '',
  description: '',
  days: WEEKDAYS.reduce((acc, d) => ({ ...acc, [d]: blankDay() }), {}),
});

export default function ShiftManagement() {
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTpl, setSavingTpl] = useState(false);
  const [savingAssign, setSavingAssign] = useState(false);

  const [tplModalOpen, setTplModalOpen] = useState(false);
  const [tplForm, setTplForm] = useState(blankTemplate());
  const [editingTplId, setEditingTplId] = useState(null);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    employee_id: '', template_id: '', effective_from: '', effective_to: '', notes: '',
  });

  // Memoised list of hourly-paid employees for the assignment dropdown.
  const hourlyEmployees = useMemo(
    () => employees.filter((e) => e.is_hourly_paid),
    [employees]
  );

  const { isAdmin } = useAuth();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, a, e] = await Promise.all([
        shiftAPI.getTemplates(),
        shiftAPI.getAssignments(),
        employeesAPI.getAll({ status: 'Active' }),
      ]);
      setTemplates(t.data || []);
      setAssignments(a.data || []);
      setEmployees(e.data || []);
    } catch (err) {
      toast.error('Failed to load shift data');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Template CRUD ----------
  const openNewTemplate = () => {
    setEditingTplId(null);
    setTplForm(blankTemplate());
    setTplModalOpen(true);
  };

  const openEditTemplate = (tpl) => {
    setEditingTplId(tpl.id);
    setTplForm({
      name: tpl.name,
      description: tpl.description || '',
      days: WEEKDAYS.reduce((acc, d) => {
        const cfg = tpl.days?.[d] || {};
        acc[d] = {
          start: cfg.start || '',
          end: cfg.end || '',
          end_next_day: !!cfg.end_next_day,
          is_off: !!cfg.is_off,
          is_overtime: !!cfg.is_overtime,
        };
        return acc;
      }, {}),
    });
    setTplModalOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!tplForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    // Validate each day
    for (const d of WEEKDAYS) {
      const day = tplForm.days[d];
      if (!day.is_off && !day.is_overtime) {
        if (!day.start || !day.end) {
          toast.error(`${d}: please set start + end OR mark as Off / Overtime-only`);
          return;
        }
      }
    }
    setSavingTpl(true);
    try {
      const payload = {
        name: tplForm.name.trim(),
        description: tplForm.description?.trim() || null,
        days: WEEKDAYS.reduce((acc, d) => {
          const day = tplForm.days[d];
          if (day.is_off) acc[d] = { is_off: true };
          else if (day.is_overtime) acc[d] = { is_overtime: true };
          else acc[d] = { start: day.start, end: day.end, end_next_day: !!day.end_next_day };
          return acc;
        }, {}),
      };
      if (editingTplId) {
        await shiftAPI.updateTemplate(editingTplId, payload);
        toast.success('Template updated');
      } else {
        await shiftAPI.createTemplate(payload);
        toast.success('Template created');
      }
      setTplModalOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTpl(false);
    }
  };

  const handleDeleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete shift template "${tpl.name}"? Cannot be undone.`)) return;
    try {
      await shiftAPI.deleteTemplate(tpl.id);
      toast.success('Template deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  // ---------- Assignment CRUD ----------
  const openNewAssignment = () => {
    setAssignForm({
      employee_id: '', template_id: '',
      effective_from: new Date().toISOString().slice(0, 10),
      effective_to: '', notes: '',
    });
    setAssignModalOpen(true);
  };

  const handleSaveAssignment = async () => {
    if (!assignForm.employee_id || !assignForm.template_id || !assignForm.effective_from) {
      toast.error('Employee, template and effective_from are required');
      return;
    }
    setSavingAssign(true);
    try {
      await shiftAPI.createAssignment({
        ...assignForm,
        effective_to: assignForm.effective_to || null,
        notes: assignForm.notes || null,
      });
      toast.success('Shift assignment created');
      setAssignModalOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create assignment');
    } finally {
      setSavingAssign(false);
    }
  };

  const handleDeleteAssignment = async (a) => {
    if (!window.confirm(`Delete this shift assignment for ${a.employee_name}?`)) return;
    try {
      await shiftAPI.deleteAssignment(a.id);
      toast.success('Assignment deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete assignment');
    }
  };

  return (
    <div className="space-y-6 page-transition" data-testid="shift-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
            <CalendarClock className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            Shift Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Define weekly shift templates and assign them per employee with effective dates.
          </p>
        </div>
      </div>

      <Tabs defaultValue="templates" className="space-y-6">
        <TabsList className="bg-secondary/30 w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="templates" data-testid="tab-templates">Shift Templates</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">Employee Assignments</TabsTrigger>
        </TabsList>

        {/* ---------- TEMPLATES TAB ---------- */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            {isAdmin() && (
              <Button onClick={openNewTemplate} data-testid="new-template-btn">
                <Plus className="w-4 h-4 mr-2" /> New Template
              </Button>
            )}
          </div>

          {loading ? (
            <div className="glass-card p-8 text-center text-muted-foreground">Loading...</div>
          ) : templates.length === 0 ? (
            <div className="glass-card p-8 text-center text-muted-foreground">No templates yet.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {templates.map((tpl) => (
                <TemplateCard
                  key={tpl.id}
                  template={tpl}
                  onEdit={() => openEditTemplate(tpl)}
                  onDelete={() => handleDeleteTemplate(tpl)}
                  canEdit={isAdmin()}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------- ASSIGNMENTS TAB ---------- */}
        <TabsContent value="assignments" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewAssignment} data-testid="new-assignment-btn">
              <Plus className="w-4 h-4 mr-2" /> Assign Shift
            </Button>
          </div>

          <div className="glass-card table-scroll">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Employee</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Effective From</TableHead>
                  <TableHead>Effective To</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : assignments.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No assignments yet.</TableCell></TableRow>
                ) : (
                  assignments.map((a) => {
                    const isCurrent = !a.effective_to || a.effective_to >= new Date().toISOString().slice(0, 10);
                    return (
                      <TableRow key={a.id} data-testid={`assignment-row-${a.id}`}>
                        <TableCell className="font-medium">{a.employee_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-purple-500/40 text-purple-300">
                            {a.template_name}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(a.effective_from)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {a.effective_to ? formatDate(a.effective_to) : (
                            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                          {a.notes || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {isAdmin() && (
                            <Button
                              variant="ghost" size="icon"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleDeleteAssignment(a)}
                              data-testid={`delete-assignment-${a.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ---------- TEMPLATE EDITOR MODAL ---------- */}
      <Dialog open={tplModalOpen} onOpenChange={setTplModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTplId ? 'Edit Shift Template' : 'New Shift Template'}</DialogTitle>
            <DialogDescription>
              Set the start/end times per weekday. Mark a day as <em>Off</em> if no work is scheduled,
              or <em>Overtime only</em> if any work that day is OT (e.g. Saturday).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tpl-name">Template Name *</Label>
                <Input
                  id="tpl-name"
                  value={tplForm.name}
                  onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
                  placeholder="e.g. Summer 5-Day Work Week"
                  data-testid="input-template-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tpl-desc">Description</Label>
                <Input
                  id="tpl-desc"
                  value={tplForm.description}
                  onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })}
                  placeholder="Optional"
                  data-testid="input-template-description"
                />
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Label>Weekly Schedule</Label>
              <p className="text-xs text-muted-foreground">
                For night shifts that span midnight, tick "Ends next day" and set the end time on the next calendar day.
              </p>
              <div className="space-y-2">
                {WEEKDAYS.map((d) => (
                  <DayRow
                    key={d}
                    day={d}
                    config={tplForm.days[d]}
                    onChange={(updates) => setTplForm({
                      ...tplForm,
                      days: { ...tplForm.days, [d]: { ...tplForm.days[d], ...updates } },
                    })}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTplModalOpen(false)} disabled={savingTpl}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={savingTpl} data-testid="save-template-btn">
              {savingTpl ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingTplId ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- ASSIGNMENT MODAL ---------- */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Shift Template</DialogTitle>
            <DialogDescription>
              Any open assignment for this employee that started earlier will be automatically
              closed the day before this one starts.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select
                value={assignForm.employee_id}
                onValueChange={(v) => setAssignForm({ ...assignForm, employee_id: v })}
              >
                <SelectTrigger data-testid="select-assignment-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {hourlyEmployees.map((e) => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>
                      {e.full_name} — {e.department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Only hourly-paid employees are listed.</p>
            </div>

            <div className="space-y-2">
              <Label>Shift Template *</Label>
              <Select
                value={assignForm.template_id}
                onValueChange={(v) => setAssignForm({ ...assignForm, template_id: v })}
              >
                <SelectTrigger data-testid="select-assignment-template">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective From *</Label>
                <Input
                  type="date"
                  value={assignForm.effective_from}
                  onChange={(e) => setAssignForm({ ...assignForm, effective_from: e.target.value })}
                  data-testid="input-effective-from"
                />
              </div>
              <div className="space-y-2">
                <Label>Effective To (optional)</Label>
                <Input
                  type="date"
                  value={assignForm.effective_to}
                  onChange={(e) => setAssignForm({ ...assignForm, effective_to: e.target.value })}
                  data-testid="input-effective-to"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={assignForm.notes}
                onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                placeholder="e.g. Rotation to night shift"
                data-testid="input-assignment-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModalOpen(false)} disabled={savingAssign}>
              Cancel
            </Button>
            <Button onClick={handleSaveAssignment} disabled={savingAssign} data-testid="save-assignment-btn">
              {savingAssign ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Create Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateCard({ template, onEdit, onDelete, canEdit }) {
  return (
    <div className="glass-card p-5 space-y-3" data-testid={`template-card-${template.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold text-base truncate">{template.name}</h3>
          {template.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{template.description}</p>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`edit-template-${template.id}`}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onDelete} className="text-red-400 hover:text-red-300" data-testid={`delete-template-${template.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-1 pt-1">
        {WEEKDAYS.map((d) => {
          const cfg = template.days?.[d] || {};
          return (
            <div key={d} className="flex items-center justify-between text-sm">
              <span className="font-mono text-muted-foreground w-10">{d}</span>
              {cfg.is_off ? (
                <Badge variant="outline" className="border-slate-500/40 text-slate-400">Off</Badge>
              ) : cfg.is_overtime ? (
                <Badge variant="outline" className="border-amber-500/40 text-amber-300">
                  <AlertTriangle className="w-3 h-3 mr-1" /> OT only
                </Badge>
              ) : (
                <span className="font-mono text-xs">
                  {cfg.start} → {cfg.end}
                  {cfg.end_next_day && <span className="text-muted-foreground ml-1">(+1d)</span>}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayRow({ day, config, onChange }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center p-2 rounded-lg bg-secondary/20">
      <div className="col-span-2 font-mono text-sm font-semibold">{day}</div>
      <div className="col-span-3 flex items-center gap-1">
        <Checkbox
          id={`off-${day}`}
          checked={config.is_off}
          onCheckedChange={(v) => onChange({ is_off: !!v, is_overtime: false })}
          data-testid={`day-off-${day}`}
        />
        <Label htmlFor={`off-${day}`} className="text-xs cursor-pointer">Off</Label>
      </div>
      <div className="col-span-3 flex items-center gap-1">
        <Checkbox
          id={`ot-${day}`}
          checked={config.is_overtime}
          onCheckedChange={(v) => onChange({ is_overtime: !!v, is_off: false })}
          data-testid={`day-ot-${day}`}
        />
        <Label htmlFor={`ot-${day}`} className="text-xs cursor-pointer">OT only</Label>
      </div>
      <div className="col-span-2">
        <Input
          type="time"
          value={config.start || ''}
          onChange={(e) => onChange({ start: e.target.value })}
          disabled={config.is_off || config.is_overtime}
          className="h-8 text-xs"
          data-testid={`day-start-${day}`}
        />
      </div>
      <div className="col-span-2">
        <Input
          type="time"
          value={config.end || ''}
          onChange={(e) => onChange({ end: e.target.value })}
          disabled={config.is_off || config.is_overtime}
          className="h-8 text-xs"
          data-testid={`day-end-${day}`}
        />
      </div>
      <div className="col-span-12 flex justify-end -mt-1">
        <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
          <Checkbox
            checked={config.end_next_day}
            onCheckedChange={(v) => onChange({ end_next_day: !!v })}
            disabled={config.is_off || config.is_overtime}
            data-testid={`day-next-${day}`}
          />
          Ends next day (overnight shift)
        </label>
      </div>
    </div>
  );
}
